/**
 * The AI "TL;DR" under a thread's root (core ThreadSummaryViewModel / logic/threadSummary): "Summary · updated 2 min
 * ago", 2–4 bullets, Refresh, an "AI-generated" label, collapsible (remembered). Without a summary yet: a Summarize
 * action; loading and error lines. Renders nothing when the card is hidden (no model on the server, turned off, no
 * replies).
 */
import type { SummaryCard } from "@bundle/core";
import { Button, Card, Icon, IconButton, Spinner, Stack, Text } from "../primitives/index.ts";
import { useT, useTr } from "../i18n/I18nProvider.tsx";

export type ThreadSummaryCardProps = {
  card: SummaryCard;
  onSummarize: () => void;
  onToggleCollapsed: () => void;
  testID: string;
};

export function ThreadSummaryCard({ card, onSummarize, onToggleCollapsed, testID }: ThreadSummaryCardProps) {
  const t = useT();
  const tr = useTr();
  if (card.kind === "hidden") return null;
  const status = card.loading ? (
    <Stack direction="horizontal" align="center" gap="1.5" testID={`${testID}-loading`}>
      <Spinner size="icon.sm" accessibilityLabel={t("summary.loading")} />
      <Text variant="footnote" color="text.secondary">
        {t("summary.loading")}
      </Text>
    </Stack>
  ) : card.failed ? (
    <Text variant="footnote" color="status.danger" testID={`${testID}-failed`}>
      {t("summary.failed")}
    </Text>
  ) : null;

  if (card.kind === "empty") {
    return (
      <Stack paddingX="5" paddingY="2">
        <Card padding="3" gap="2" testID={testID}>
          <Stack direction="horizontal" align="center" gap="2">
            <Icon name="sparkles" size="icon.sm" color="accent.text" />
            <Stack grow>
              <Text variant="footnote" color="text.secondary">
                {t("summary.empty")}
              </Text>
            </Stack>
            <Button label={t("summary.summarize")} icon="sparkles" size="sm" variant="secondary" onPress={onSummarize} disabled={card.loading} testID={`${testID}-summarize`} />
          </Stack>
          {status}
        </Card>
      </Stack>
    );
  }

  return (
    <Stack paddingX="5" paddingY="2">
      <Card padding="3" gap="2" testID={testID}>
        <Stack direction="horizontal" align="center" gap="2">
          <Icon name="sparkles" size="icon.sm" color="accent.text" />
          <Text variant="footnote" weight="semibold">
            {t("summary.title")}
          </Text>
          <Stack grow>
            <Text variant="footnote" color="text.secondary" lines={1} testID={`${testID}-updated`}>
              {`· ${tr(card.updated)}`}
            </Text>
          </Stack>
          <IconButton
            icon="reload"
            size="sm"
            variant={card.stale ? "filled" : "ghost"}
            accessibilityLabel={card.stale ? `${t("summary.refresh")} (${t("summary.stale")})` : t("summary.refresh")}
            onPress={onSummarize}
            disabled={card.loading}
            testID={`${testID}-refresh`}
          />
          <IconButton
            icon={card.collapsed ? "chevronDown" : "chevronUp"}
            size="sm"
            accessibilityLabel={t(card.collapsed ? "summary.expand" : "summary.collapse")}
            onPress={onToggleCollapsed}
            testID={`${testID}-toggle`}
          />
        </Stack>
        {card.collapsed ? null : (
          <Stack gap="1" testID={`${testID}-bullets`}>
            {card.bullets.map((b, i) => (
              <Stack key={i} direction="horizontal" gap="2" align="start">
                <Text color="text.tertiary">•</Text>
                <Stack grow>
                  <Text variant="body">{b}</Text>
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}
        {status}
        {card.collapsed ? null : (
          <Text variant="caption" color="text.tertiary" testID={`${testID}-ai-label`}>
            {t("summary.aiLabel")}
          </Text>
        )}
      </Card>
    </Stack>
  );
}
